import {
  AlertCircle,
  Calendar,
  HelpCircle,
  Layers,
  Plus,
  Settings2,
  Tag,
  Trash2,
  Type,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CurrencyInput } from "@/components/ui/currency-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { CouponType, DiscountFormData, DiscountRuleFormData } from "../types";
import { createDefaultRule, getDefaultDiscountFormData } from "../types";
import { type DiscountValidationErrors, validateDiscountDatePeriods } from "../utils/validation";
import { ActivePeriodSection } from "./active-period-section";
import { DiscountAppliesSection } from "./discount-applies-section";
import { DiscountValueSection } from "./discount-value-section";
import { OffPeakHoursSection } from "./off-peak-hours-section";

function HintIcon({ text }: { text: string }) {
  const [open, setOpen] = useState(false);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center -m-1 p-1 min-w-[28px] min-h-[28px] text-muted-foreground hover:text-foreground"
            onClick={() => setOpen((prev) => !prev)}
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface DiscountFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (data: DiscountFormData) => void;
  initialData?: Partial<DiscountFormData>;
  loading?: boolean;
  isEditing?: boolean;
}

export function DiscountForm({
  open,
  onOpenChange,
  onSubmit,
  initialData,
  loading = false,
  isEditing = false,
}: DiscountFormProps) {
  const { t } = useTranslation("discounts");

  // Form state - reset when opening with new data
  const [formData, setFormData] = useState<DiscountFormData>(() => ({
    ...getDefaultDiscountFormData(),
    ...initialData,
  }));

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState<DiscountValidationErrors>({});

  // Error focus ref
  const errorAlertRef = useRef<HTMLDivElement>(null);

  // Update form when initialData changes or dialog opens
  useEffect(() => {
    if (open) {
      const defaults = getDefaultDiscountFormData();
      const targetStartDateTime =
        initialData?.targetStartDateTime ?? defaults.targetStartDateTime ?? defaults.startDateTime;
      const targetEndDateTime =
        initialData?.targetEndDateTime ?? defaults.targetEndDateTime ?? defaults.endDateTime;
      const startDateTime = initialData?.startDateTime ?? targetStartDateTime;
      const endDateTime = initialData?.endDateTime ?? targetEndDateTime;

      const mergedData: DiscountFormData = {
        ...defaults,
        ...initialData,
        targetStartDateTime,
        targetEndDateTime,
        startDateTime,
        endDateTime,
      };

      const hasCustomVisibilityPeriod =
        !startDateTime.equals(targetStartDateTime) || !endDateTime.equals(targetEndDateTime);

      setFormData({
        ...mergedData,
        hasVisibilityPeriod: hasCustomVisibilityPeriod || false,
      });
      setValidationErrors({});
    }
  }, [open, initialData]);

  // Update helper functions
  const updateField = <K extends keyof DiscountFormData>(field: K, value: DiscountFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateRule = (index: number, updates: Partial<DiscountRuleFormData>) => {
    setFormData((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, i) => (i === index ? { ...rule, ...updates } : rule)),
    }));
  };

  const addRule = () => {
    if (formData.rules.length >= 10) return;
    setFormData((prev) => ({
      ...prev,
      rules: [...prev.rules, createDefaultRule()],
    }));
  };

  const removeRule = (index: number) => {
    if (formData.rules.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = () => {
    const errors = validateDiscountDatePeriods(
      formData.startDateTime,
      formData.endDateTime,
      formData.hasTimeInterval,
      formData.hasOffPeakHours,
      formData.targetStartDateTime,
      formData.targetEndDateTime,
      formData.offPeakHours
    );

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setTimeout(() => {
        errorAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        errorAlertRef.current?.focus();
      }, 0);
      return;
    }

    if (formData.hasVisibilityPeriod && formData.startDateTime && formData.targetEndDateTime) {
      if (formData.startDateTime > formData.targetEndDateTime) {
        const newErrors = {
          ...errors,
          activePeriod: t("form.validation.visibilityPeriodAfterBooking"),
        };
        setValidationErrors(newErrors);
        setTimeout(() => {
          errorAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
          errorAlertRef.current?.focus();
        }, 0);
        return;
      }
    }

    const targetStartDateTime = formData.targetStartDateTime ?? formData.startDateTime;
    const targetEndDateTime = formData.targetEndDateTime ?? formData.endDateTime;

    const submissionData: DiscountFormData = {
      ...formData,
      hasTimeInterval: !!formData.hasVisibilityPeriod,
      targetStartDateTime,
      targetEndDateTime,
      startDateTime: formData.hasVisibilityPeriod ? formData.startDateTime : targetStartDateTime,
      endDateTime: formData.hasVisibilityPeriod ? formData.endDateTime : targetEndDateTime,
      hasVisibilityPeriod: undefined,
    };

    setValidationErrors({});
    onSubmit?.(submissionData);
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setFormData({ ...getDefaultDiscountFormData(), ...initialData });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 flex flex-col overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-3 border-b flex-shrink-0">
          <DialogTitle>{isEditing ? t("form.editDialogTitle") : t("form.dialogTitle")}</DialogTitle>
          <DialogDescription>{t("form.description")}</DialogDescription>
        </DialogHeader>

        {/* Active/Inactive Toggle - Only show when editing */}
        {isEditing && (
          <div className="px-6 py-3 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="discount-active" className="text-base">
                  {formData.isActive ? t("form.status.active") : t("form.status.inactive")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {formData.isActive
                    ? t("form.status.activeDescription")
                    : t("form.status.inactiveDescription")}
                </p>
              </div>
              <Switch
                id="discount-active"
                checked={formData.isActive ?? false}
                onCheckedChange={(checked) => updateField("isActive", checked)}
                disabled={loading}
              />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6">
          <div className="pt-2 pb-4 space-y-4">
            {/* Validation Errors Alert */}
            {(validationErrors.activePeriod ||
              validationErrors.targetedPeriod ||
              validationErrors.general) && (
              <Alert variant="destructive" ref={errorAlertRef} tabIndex={-1}>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    {validationErrors.activePeriod && (
                      <div>
                        <strong>Visibility Period:</strong> {validationErrors.activePeriod}
                      </div>
                    )}
                    {validationErrors.targetedPeriod && (
                      <div>
                        <strong>Booking Period:</strong> {validationErrors.targetedPeriod}
                      </div>
                    )}
                    {validationErrors.general && <div>{validationErrors.general}</div>}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Section 1: Title */}
            <div className="space-y-1">
              <Label htmlFor="discount-title" className="flex items-center gap-1.5">
                <Type className="h-3.5 w-3.5 text-blue-500" />
                {t("form.titleField.label")}
                <HintIcon text={t("form.titleField.description")} />
              </Label>
              <Input
                id="discount-title"
                value={formData.title}
                onChange={(e) => updateField("title", e.target.value)}
                placeholder={t("form.titleField.placeholder")}
                disabled={loading}
              />
            </div>

            {/* Section 2: Coupon Type (only for coupons) */}
            {!formData.autoApply && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="coupon-type">{t("form.couponType.label")}</Label>
                  <Select
                    value={formData.couponType}
                    onValueChange={(value) => updateField("couponType", value as CouponType)}
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        {formData.couponType === "public"
                          ? t("form.couponType.public")
                          : t("form.couponType.private")}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">{t("form.couponType.public")}</SelectItem>
                      <SelectItem value="private">{t("form.couponType.private")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.couponType === "public" && (
                  <div className="space-y-1">
                    <Label htmlFor="discount-code" className="flex items-center gap-1.5">
                      {t("form.code.label")}
                      <HintIcon text={t("form.code.description")} />
                    </Label>
                    <Input
                      id="discount-code"
                      value={formData.code ?? ""}
                      onChange={(e) => updateField("code", e.target.value.toUpperCase())}
                      placeholder={t("form.code.placeholder")}
                      disabled={loading}
                    />
                  </div>
                )}
              </div>
            )}

            <Separator />

            {/* Section 3: Apply To */}
            <div className="space-y-1">
              <Label className="flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-violet-500" />
                {t("form.applyTo.label")}
              </Label>
              <Select
                value={formData.applyTo}
                onValueChange={(value) => {
                  const applyTo = value as "per_item" | "total";
                  updateField("applyTo", applyTo);
                  // When switching to total, collapse to single rule targeting all services
                  if (applyTo === "total") {
                    const firstRule = formData.rules[0] ?? createDefaultRule();
                    setFormData((prev) => ({
                      ...prev,
                      applyTo,
                      rules: [{ ...firstRule, targetType: "all_services", selectedServiceIds: [] }],
                    }));
                  }
                }}
                disabled={loading}
              >
                <SelectTrigger>
                  <SelectValue>
                    {formData.applyTo === "total"
                      ? t("form.applyTo.total")
                      : t("form.applyTo.perItem")}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_item">{t("form.applyTo.perItem")}</SelectItem>
                  <SelectItem value="total">{t("form.applyTo.total")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Section 4: Rules */}
            {formData.applyTo === "total" ? (
              // Cart total: single value, no target selector
              <div>
                <DiscountValueSection
                  valueType={formData.rules[0]?.valueType ?? "percentage"}
                  onValueTypeChange={(type) => updateRule(0, { valueType: type })}
                  value={formData.rules[0]?.value ?? 0}
                  onValueChange={(value) => updateRule(0, { value })}
                  disabled={loading}
                />
              </div>
            ) : (
              // Per item: full rules UI with multiple rules + targets
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1.5">
                    <Tag className="h-3.5 w-3.5 text-emerald-500" />
                    {t("form.rules.label")}
                  </Label>
                  {formData.rules.length < 10 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addRule}
                      disabled={loading}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {t("form.rules.addRule")}
                    </Button>
                  )}
                </div>

                {formData.rules.map((rule, index) => (
                  <div key={index} className="border rounded-lg p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {t("form.rules.ruleNumber", { number: index + 1 })}
                      </span>
                      {formData.rules.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRule(index)}
                          disabled={loading}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>

                    <DiscountValueSection
                      valueType={rule.valueType}
                      onValueTypeChange={(type) => updateRule(index, { valueType: type })}
                      value={rule.value}
                      onValueChange={(value) => updateRule(index, { value })}
                      disabled={loading}
                    />

                    <DiscountAppliesSection
                      appliesTo={rule.targetType === "all_services" ? "all" : "specific_services"}
                      onAppliesToChange={(value) =>
                        updateRule(index, {
                          targetType: value === "all" ? "all_services" : "specific_services",
                          selectedServiceIds: value === "all" ? [] : rule.selectedServiceIds,
                        })
                      }
                      selectedServiceIds={rule.selectedServiceIds}
                      onServiceIdsChange={(ids) => updateRule(index, { selectedServiceIds: ids })}
                      discountValueType={rule.valueType}
                      discountValue={rule.value}
                      disabled={loading}
                    />
                  </div>
                ))}
              </div>
            )}

            <Separator />

            {/* Section 5: Booking Period & Conditions */}
            <div className="space-y-3">
              {/* Booking Period (Required) */}
              <div className="space-y-1">
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-amber-500" />
                  {t("form.bookingPeriod.label")}
                  <HintIcon text={t("form.bookingPeriod.description")} />
                </Label>
                <ActivePeriodSection
                  title=""
                  startDateTime={formData.targetStartDateTime ?? formData.startDateTime}
                  hasError={!!validationErrors.targetedPeriod}
                  onStartDateTimeChange={(dateTime) => updateField("targetStartDateTime", dateTime)}
                  endDateTime={formData.targetEndDateTime ?? formData.endDateTime}
                  onEndDateTimeChange={(dateTime) => updateField("targetEndDateTime", dateTime)}
                  disabled={loading}
                />
              </div>

              {/* Minimum Spent Field */}
              <div className="space-y-1">
                <Label htmlFor="min-spent" className="flex items-center gap-1.5">
                  {t("form.minSpent.label")}
                  <span className="text-muted-foreground font-normal">
                    ({t("common:optional")})
                  </span>
                  <HintIcon text={t("form.minSpent.description")} />
                </Label>
                <CurrencyInput
                  id="min-spent"
                  value={formData.minSpent}
                  onValueChange={(v) => updateField("minSpent", v === 0 ? undefined : v)}
                  disabled={loading}
                  showSymbol={true}
                />
              </div>
            </div>

            <Separator />

            {/* Section 6: Advanced Conditions (Optional) */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium flex items-center gap-1.5">
                <Settings2 className="h-3.5 w-3.5 text-slate-500" />
                {t("form.advancedConditions.title")}
                <HintIcon text={t("form.advancedConditions.description")} />
              </h4>

              {/* Visibility Period Option */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="has-visibility-period"
                    checked={formData.hasVisibilityPeriod ?? false}
                    onCheckedChange={(checked) =>
                      updateField("hasVisibilityPeriod", checked === true)
                    }
                    disabled={loading}
                  />
                  <Label
                    htmlFor="has-visibility-period"
                    className="text-sm font-medium cursor-pointer flex items-center gap-1.5"
                  >
                    {t("form.advancedConditions.visibilityPeriod")}
                    <HintIcon text={t("form.advancedConditions.visibilityPeriodDescription")} />
                  </Label>
                </div>

                {formData.hasVisibilityPeriod && (
                  <div className="pl-6 border-l-2 border-muted">
                    <ActivePeriodSection
                      title={t("form.advancedConditions.visibilityPeriodRange")}
                      startDateTime={formData.startDateTime}
                      hasError={!!validationErrors.activePeriod}
                      onStartDateTimeChange={(dateTime) => updateField("startDateTime", dateTime)}
                      endDateTime={formData.endDateTime}
                      onEndDateTimeChange={(dateTime) => updateField("endDateTime", dateTime)}
                      disabled={loading}
                    />
                  </div>
                )}
              </div>

              {/* Off-Peak Hours Option */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="has-off-peak-hours"
                    checked={formData.hasOffPeakHours}
                    onCheckedChange={(checked) => updateField("hasOffPeakHours", checked === true)}
                    disabled={loading}
                  />
                  <Label
                    htmlFor="has-off-peak-hours"
                    className="text-sm font-medium cursor-pointer flex items-center gap-1.5"
                  >
                    {t("form.advancedConditions.offPeakHours")}
                    <HintIcon text={t("form.advancedConditions.offPeakHoursDescription")} />
                  </Label>
                </div>

                {formData.hasOffPeakHours && (
                  <div className="pl-6 border-l-2 border-muted">
                    <OffPeakHoursSection
                      schedules={formData.offPeakHours}
                      onSchedulesChange={(schedules) => updateField("offPeakHours", schedules)}
                      disabled={loading}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t flex gap-3 justify-end flex-shrink-0">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            {t("common:actions.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading
              ? t("common:actions.saving")
              : isEditing
                ? t("form.saveChanges")
                : t("form.submit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
